import { FC } from 'react';

import { breakpointsToSrcSet } from './breakpointsToSrcSet/breakpointsToSrcSet';
import { encodeImageSrc, getCarmaGenericImageLoader, hasImageLoader } from './carmaGenericImageLoader/carmaGenericImageLoader';

import type { CarmaImageFit, ImageBreakpointSrcSetSizes } from './Image.type';

type Props = {
  src: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  className?: string;
  breakpoints?: ImageBreakpointSrcSetSizes;
  fit?: CarmaImageFit;
  'data-testid'?: string;
};

/**
 * Wrapper around NextJS Image component to allow dynamic selection of the image loader
 */
export const Image: FC<Props> = ({ priority, breakpoints, alt, fit, ...props }) => {
  if (typeof props.src === 'string' && (hasImageLoader(props.src) || isSvg(props.src))) {
    const encodedSrc = encodeImageSrc(props.src);
    const responsiveImageProps = getResponsiveImageProps(encodedSrc, props.width, props.height, fit, breakpoints);

    return (
      <img
        {...props}
        {...responsiveImageProps}
        decoding="async"
        fetchPriority={priority ? 'high' : undefined}
        loading={priority ? 'eager' : 'lazy'}
        alt={alt}
      />
    );
  }

  // Defaults to img
  return <img {...props} alt={alt} />;
};

const RETINA_DENSITY = 2;

const getResponsiveImageProps = (
  src: string,
  width: number,
  height?: number,
  fit?: CarmaImageFit,
  breakpoints?: ImageBreakpointSrcSetSizes,
): { src: string; srcSet?: string; sizes?: string } => {
  if (isSvg(src)) {
    return { src };
  }

  if (typeof breakpoints === 'undefined' || breakpoints.length <= 1) {
    const generatedSet = generatePxDensitySrcSet(src, width, height, fit);
    return {
      src: generatedSet[0],
      srcSet: generatedSet[0] !== generatedSet[1] ? generatedSet[1] : undefined,
    };
  }

  const loader = getCarmaGenericImageLoader(src);
  const aspectRatio = height ? height / width : undefined;
  const layoutSizes = breakpointsToSrcSet(breakpoints);
  const layoutWidths = [...new Set(layoutSizes.map(([, layoutWidth]) => layoutWidth))];
  const srcSetWidths = [...new Set(layoutWidths.flatMap((layoutWidth) => [layoutWidth, layoutWidth * RETINA_DENSITY]))].sort(
    (a, b) => a - b,
  );
  const srcSet = srcSetWidths
    .map((srcSetWidth) => {
      return `${loader({ src, width: srcSetWidth, height: aspectRatio ? Math.floor(srcSetWidth * aspectRatio) : undefined, fit })} ${srcSetWidth}w`;
    })
    .join(', ');
  const sizes = layoutSizes
    .map(([mediaQuery, layoutWidth]) => (mediaQuery ? `${mediaQuery} ${layoutWidth}px` : `${layoutWidth}px`))
    .join(', ');
  const smallestLayoutWidth = Math.min(...layoutWidths);

  return {
    src: loader({
      src,
      width: smallestLayoutWidth,
      height: aspectRatio ? Math.floor(smallestLayoutWidth * aspectRatio) : undefined,
      fit,
    }),
    srcSet,
    sizes,
  };
};

/** Generate the srcSet based on the pixel density */
const generatePxDensitySrcSet = (src: string, width: number, height?: number, fit?: CarmaImageFit) => {
  const loader = getCarmaGenericImageLoader(src);
  const image1X = loader({ src: src, width, height, fit });
  const image2X = loader({ src: src, width: width * RETINA_DENSITY, height: height ? height * RETINA_DENSITY : undefined, fit });

  return [image1X, `${image1X} 1x, ${image2X} 2x`];
};

const isSvg = (src: string) => src.endsWith('.svg');
